import dayjs, { Dayjs } from "dayjs";
import CurrencyDto from "../../../dto/Settings/CurrencyDto";
import {
  ManagementDebtChangedDealDto,
  ManagementDebtDealImpactDto,
  ManagementDebtSameDayPaidAccountDto,
  ManagementDebtSameDayPaidDealDto,
  ManagementSettlementAccountChangeDto,
} from "../../../dto/ManagementReports/ManagementReportDetailsDto";

export const convertReportDetailDate = (date: string): Dayjs => dayjs(date);

type SameDayPaidAccount = Omit<ManagementDebtSameDayPaidAccountDto, "creationDate" | "paymentDate"> & {
  creationDate: Dayjs;
  paymentDate: Dayjs;
};

export type SameDayPaidDeal = Omit<ManagementDebtSameDayPaidDealDto, "accounts"> & {
  accounts: Array<SameDayPaidAccount>;
};

export type SameDayPaidAccountRow = Omit<SameDayPaidDeal, "accounts"> &
  SameDayPaidAccount & {
    accountIndex: number;
    accountCount: number;
  };

export type SameDayPaidAccountView = SameDayPaidAccountRow & {
  paidAmountInSelectedCurrency: number | null;
  contractorName: string;
  contractorNameType?: "danger";
};

export const convertSameDayPaidDeals = (deals: Array<ManagementDebtSameDayPaidDealDto>): Array<SameDayPaidDeal> =>
  deals.map((deal) => ({
    ...deal,
    accounts: deal.accounts.map((account) => ({
      ...account,
      creationDate: convertReportDetailDate(account.creationDate),
      paymentDate: convertReportDetailDate(account.paymentDate),
    })),
  }));

export const flattenSameDayPaidDeals = (deals: Array<SameDayPaidDeal>): Array<SameDayPaidAccountRow> =>
  deals.flatMap(({ accounts, ...deal }) =>
    accounts.map((account, accountIndex) => ({ ...deal, ...account, accountIndex, accountCount: accounts.length })),
  );

export const convertPaidAccountAmount = (row: SameDayPaidAccountRow, currencyId: number | undefined, currencies: Array<CurrencyDto>) => {
  if (currencyId === row.baseCurrencyId) return Number(row.paidAmountInBaseCurrency);
  if (currencyId === row.currencyId) return Number(row.paidAmount);
  const sourceCurrency = currencies.find((currency) => currency.id === row.baseCurrencyId);
  const targetCurrency = currencies.find((currency) => currency.id === currencyId);
  if (
    !sourceCurrency ||
    !targetCurrency ||
    sourceCurrency.baseCurrencyId !== targetCurrency.baseCurrencyId ||
    !Number.isFinite(sourceCurrency.rate) ||
    sourceCurrency.rate <= 0 ||
    !Number.isFinite(targetCurrency.rate) ||
    targetCurrency.rate <= 0 ||
    !Number.isFinite(sourceCurrency.nominal) ||
    sourceCurrency.nominal <= 0 ||
    !Number.isFinite(targetCurrency.nominal) ||
    targetCurrency.nominal <= 0
  ) {
    return null;
  }
  return (
    (Number(row.paidAmountInBaseCurrency) * sourceCurrency.rate * targetCurrency.nominal) / (sourceCurrency.nominal * targetCurrency.rate)
  );
};

export const getPaidAccountDealRowSpan = (row: SameDayPaidAccountRow, pageRowIndex: number, pageSize: number) =>
  row.accountIndex === 0 || pageRowIndex === 0 ? Math.min(row.accountCount - row.accountIndex, pageSize - pageRowIndex) : 0;

export type FinancialDetailsRequest = {
  requestId: string;
  date: Dayjs;
  companyIds: Array<number>;
};

export type FinancialDetailsState = {
  request?: FinancialDetailsRequest;
  loading: boolean;
  error: boolean;
  deals: Array<ManagementDebtDealImpactDto>;
  changedDeals: Array<ManagementDebtChangedDealDto>;
  sameDayPaidDeals: Array<SameDayPaidDeal>;
  accounts: Array<ManagementSettlementAccountChangeDto>;
};

export const createEmptyDetails = (): FinancialDetailsState => ({
  loading: false,
  error: false,
  deals: [],
  changedDeals: [],
  sameDayPaidDeals: [],
  accounts: [],
});

export const sumDebtDetails = (rows: Array<ManagementDebtDealImpactDto>) => {
  const totals = new Map<number, { currencyId: number; debit: number; credit: number }>();
  rows.forEach((row) => {
    const total = totals.get(row.baseCurrencyId) || { currencyId: row.baseCurrencyId, debit: 0, credit: 0 };
    total.debit += Number(row.debit ?? 0);
    total.credit += Number(row.credit ?? 0);
    totals.set(row.baseCurrencyId, total);
  });
  return [...totals.values()];
};

export const sumSettlementAccountChanges = (rows: Array<ManagementSettlementAccountChangeDto>) => {
  const totals = new Map<number, { currencyId: number; currency: string; previousAmount: number; currentAmount: number }>();
  rows.forEach((row) => {
    const total = totals.get(row.currencyId) || { currencyId: row.currencyId, currency: row.currency, previousAmount: 0, currentAmount: 0 };
    total.previousAmount += Number(row.previousAmount ?? 0);
    total.currentAmount += Number(row.currentAmount ?? 0);
    totals.set(row.currencyId, total);
  });
  return [...totals.values()];
};

export const sumSameDayPaidAccounts = (rows: Array<SameDayPaidAccountView>) =>
  rows.reduce<{ revenue: number | null; expense: number | null }>(
    (totals, row) => {
      const field = row.type === "REVENUE" ? "revenue" : "expense";
      const amount = row.paidAmountInSelectedCurrency;
      totals[field] = totals[field] !== null && amount !== null ? totals[field] + amount : null;
      return totals;
    },
    { revenue: 0, expense: 0 },
  );

export const accountChange = (row: ManagementSettlementAccountChangeDto) => Number(row.currentAmount) - Number(row.previousAmount);
